/**
 * `normalizeTitular` — forma canónica del nombre del propietario.
 *
 * Política institucional (mayo 2026): el "Nombre completo" del propietario
 * se persiste SIEMPRE en mayúsculas, con espacios colapsados y `.normalize('NFC')`
 * para que cualquier consulta del titular en cualquier parte del sistema
 * (header de Captura, tabla Expedientes, panel Dashboard cliente, KMLs
 * exportados, PDFs generados, queries SQL) lea el mismo valor canónico.
 *
 * Sin este helper, el cleartext que viaja al RPC y al cifrado AES-256-GCM
 * preservaba la capitalización tal-cual la tipeó el técnico ("María López"
 * un día, "MARIA LOPEZ" el siguiente) → dos versiones cifradas de la misma
 * persona, lookups por `titular_v2 == X` rotos, y máscaras inconsistentes
 * entre re-capturas.
 *
 * NFC primero: aunque PostgreSQL tolera ambas formas, la búsqueda exacta
 * sobre bytes cifrados depende del cleartext byte-a-byte; sin NFC, "María"
 * (precomposed `í`) y "María" (a + combining acute) cifran a bytes
 * distintos. NFC normaliza antes de el `.toUpperCase()`.
 *
 * Idempotente: aplicar dos veces da el mismo resultado.
 *
 * Devuelve string vacío para entradas vacías/whitespace-only (nunca null,
 * para que sea seguro encadenar). Caller decide si convertir "" a null.
 */
export function normalizeTitular(raw: string | null | undefined): string {
  if (raw == null) return '';
  const t = String(raw).trim();
  if (t === '') return '';
  // NFC primero (ver doc arriba), luego uppercase, luego colapsar espacios
  // múltiples / tabs / NBSPs a un solo espacio.
  return t.normalize('NFC').toUpperCase().replace(/\s+/g, ' ').trim();
}

/**
 * `buildTitularMasked` — deriva el texto enmascarado del titular para
 * mostrar en UI sin exponer el nombre completo.
 *
 * Regla espejo del path SQL en `0020_seed_inventario_smart_upsert.sql`
 * (líneas 63-71, donde recupera el titular del campo `reunion_fecha`
 * del legacy bug):
 *
 *   - `>=3` palabras → `PRIMER S. T.` (primer palabra completa +
 *     iniciales de las 2 siguientes con punto).
 *   - `2` palabras   → `PRIMER S.`     (primer palabra + inicial de la
 *     segunda; añadido aquí para mejorar el caso "MARIA LOPEZ").
 *   - `1` palabra    → la palabra tal cual (en mayúsculas).
 *   - vacío / null   → null (no hay máscara).
 *
 * Por qué un helper TS y no el SQL:
 *   - El BFF llama `seed_propietario_inventario` y `upsertPropietarioV2`
 *     en dos pasos; ambos deben mantener `titular_masked` consistente.
 *     Es más simple computar la máscara una vez en TS y pasarla en el
 *     payload de ambos, que duplicar la lógica en SQL.
 *   - Tests unitarios sin infraestructura de DB.
 *   - La regla legacy en SQL sigue siendo válida como fallback histórico
 *     para datos que se sembraron antes de este cambio.
 *
 * Seguridad: el resultado NO contiene el nombre completo. Es seguro
 * loguearlo y mostrarlo en cabeceras / listados.
 *
 * Internamente delega en `normalizeTitular` para que la regla UPPERCASE
 * + NFC + espacios colapsados sea exactamente la misma que se aplica al
 * cleartext que viaja al cifrado.
 */

export function buildTitularMasked(titular: string | null | undefined): string | null {
  const upper = normalizeTitular(titular);
  if (upper === '') return null;
  const parts = upper.split(' ').filter((p) => p.length > 0);
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0]!;
  if (parts.length === 2) return `${parts[0]} ${parts[1]!.charAt(0)}.`;
  // 3 o más: primer palabra + iniciales de las 2 siguientes (espejo SQL).
  // Si vienen 4+ palabras (apellido compuesto), las extras se ignoran:
  // mantenemos la consistencia con el legacy "PRIMER S. T." de 3 segmentos.
  return `${parts[0]} ${parts[1]!.charAt(0)}. ${parts[2]!.charAt(0)}.`;
}
